import React, { useState, useEffect, useContext } from "react";
import { Container, Row, Col } from "react-bootstrap";
import {
  MdDeliveryDining,
  MdClose,
  MdReceiptLong,
  MdStorefront,
  MdLocationOn,
  MdPerson,
  MdPhone,
  MdCalendarToday,
  MdShoppingBag,
  MdCheckCircle,
  MdAssignmentReturn,
  MdLocalShipping,
  MdCameraAlt,
} from "react-icons/md";
import { FaStar, FaRegStar } from "react-icons/fa";
import Swal from "sweetalert2";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import JobCard from "../../../components/deliveryPanel/jobCard/JobCard";
import "../../../assets/css/delivery/myJob/MyJob.css";
import DeliveryPanelLayout from "../../../layouts/DeliveryPanelLayout";
import MyContext from "../../../context/MyContext";
import { MyJobsPageSkeleton } from "../../../components/general/skeleton/SkeletonBlock";

// ── Helper: initials from name ──
const getInitials = (name = "") =>
  name
    .split(" ")
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

const TABS = [
  { key: "all", label: "All" },
  { key: "assigned", label: "Assigned" },
  { key: "ready_for_pickup", label: "Ready for Pickup" },
  { key: "shipping", label: "Out for Delivery" },
  { key: "arrived", label: "Arrived" },
  { key: "completed", label: "Completed" },
  { key: "cancelled", label: "Cancelled" },
  { key: "returned", label: "Returned" },
];

const ETA_OPTIONS = [
  "Within 1 hour",
  "1-2 hours",
  "2-4 hours",
  "4-8 hours",
  "Same day",
  "Next day",
];

const normalizeStatus = (status) => (status || "").toString().trim().toLowerCase();

const MyJobs = () => {
  const navigate = useNavigate();
  const { baseUrl, baseUrlForFiles, loggedInDeliveryMan, authToken } =
    useContext(MyContext);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("all");
  const [allJobs, setAllJobs] = useState([]);
  // ── Modal states — both live here in MyJob.jsx ──
  const [selectedJob, setSelectedJob] = useState(null);
  const [selectedReview, setSelectedReview] = useState(null);
  const [markingPickedUp, setMarkingPickedUp] = useState(false);

  // ── Image-Verification Checkpoint 2 (Confirm Pickup) — inline capture,
  // ab Swal ke andar nahi, seedha odm-modal ke footer mein, taake overlay
  // stacking ki wajah se input peeche chhup na jaye ──
  const [captureMode, setCaptureMode] = useState(null); // null | "pickup"
  const [pickupImageFile, setPickupImageFile] = useState(null);
  const [pickupImagePreview, setPickupImagePreview] = useState(null);
  const [pickupEta, setPickupEta] = useState("");
  const [pickupImageError, setPickupImageError] = useState("");
  const [pickupEtaError, setPickupEtaError] = useState("");

  // ── OTP-based delivery confirmation ──
  const [otpJobId, setOtpJobId] = useState(null);
  const [otpValue, setOtpValue] = useState("");
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);

  const resetPickupCapture = () => {
    setPickupImageFile(null);
    setPickupImagePreview(null);
    setPickupEta("");
    setPickupImageError("");
    setPickupEtaError("");
  };

  const closeJobModal = () => {
    setSelectedJob(null);
    setCaptureMode(null);
    resetPickupCapture();
  };

  // Lock background scroll while any modal is open
  const viewMyJobs = () => {
    if (!authToken) {
      setLoading(false);
      navigate("/delivery/login");
      return;
    }
    if (!loggedInDeliveryMan?.id) return;
    setLoading(true);
    axios
      .get(`${baseUrl}/delivery/jobs/viewMyJobs`, {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      })
      .then((response) => {
        setLoading(false);
        if (response.data.hasOwnProperty("error")) {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: response.data.error,
          });
        } else {
          setAllJobs(response.data.jobs || []);
        }
      })
      .catch((error) => {
        setLoading(false);
        if (error.response?.status === 401) {
          window.location.href = "/delivery/login";
          return;
        }
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to load my jobs.",
        });
      });
  };
  useEffect(() => {
    viewMyJobs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedInDeliveryMan?.id, authToken]);
  const updateJobStatus = (jobId, newStatus) => {
    const formData = new FormData();
    formData.append("id", jobId);
    formData.append("status", newStatus);

    axios
      .post(`${baseUrl}/delivery/jobs/updateJobStatus`, formData, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((res) => {
        if (res.data.success) {
          Swal.fire({
            icon: "success",
            title: "Success",
            text: "Status updated successfully!",
          });

          viewMyJobs();
        } else {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: res.data.message,
          });
        }
      })
      .catch((error) => {
        if (error.response?.status === 401) {
          window.location.href = "/delivery/login";
        }
      });
  };

  // ══════════════════════════════════════════════════════
  // markPickedUp — jobId, image file, aur eta select hone ke
  // baad hi call hota hai (handleConfirmPickup se). Sirf
  // "ready_for_pickup" jobs par dikhta hai. Driver confirm
  // karta hai ke usne seller se product physically utha liya hai —
  // job "ready_for_pickup" se "shipping" mein chala jata hai.
  // ══════════════════════════════════════════════════════
  const markPickedUp = (jobId, imageFile, eta) => {
    setMarkingPickedUp(true);
    const formData = new FormData();
    formData.append("id", jobId);
    formData.append("pickup_confirmation_image", imageFile);
    formData.append("estimated_delivery_time", eta);

    axios
      .post(`${baseUrl}/delivery/jobs/markPickedUp`, formData, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((res) => {
        setMarkingPickedUp(false);
        if (res.data.hasOwnProperty("error") || res.data.success === false) {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: res.data.error || res.data.message,
          });
          return;
        }
        Swal.fire({
          icon: "success",
          title: "Success",
          text: "Pickup confirmed — delivery started!",
        });
        closeJobModal();
        viewMyJobs();
      })
      .catch((error) => {
        setMarkingPickedUp(false);
        if (error.response?.status === 401) {
          window.location.href = "/delivery/login";
          return;
        }
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to update status.",
        });
      });
  };

  const handleConfirmPickup = () => {
    let hasError = false;
    if (!pickupImageFile) {
      setPickupImageError("Please attach a photo of the picked-up product.");
      hasError = true;
    } else {
      setPickupImageError("");
    }
    if (!pickupEta) {
      setPickupEtaError("Please select an estimated delivery time.");
      hasError = true;
    } else {
      setPickupEtaError("");
    }
    if (hasError) return;
    markPickedUp(selectedJob.id, pickupImageFile, pickupEta);
  };

  // ══════════════════════════════════════════════════════
  // NEW — markArrived
  // Driver delivery-point par pahunch kar dabata hai. Job "shipping"
  // se "arrived" mein jata hai — product abhi unload nahi hota,
  // customer outdoor check karega (Reject/Return option ab isi
  // status ke dauran, OTP se pehle). Koi image/confirmation nahi
  // chahiye is step ke liye, bas ek click.
  // ══════════════════════════════════════════════════════
  const markArrived = (jobId) => {
    const formData = new FormData();
    formData.append("id", jobId);

    axios
      .post(`${baseUrl}/delivery/jobs/markArrived`, formData, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((res) => {
        if (res.data.hasOwnProperty("error") || res.data.success === false) {
          Swal.fire({ icon: "error", title: "Error", text: res.data.error || res.data.message });
          return;
        }
        Swal.fire({ icon: "success", title: "Marked as Arrived", text: "Waiting for the customer to check the order." });
        viewMyJobs();
      })
      .catch((error) => {
        if (error.response?.status === 401) {
          window.location.href = "/delivery/login";
          return;
        }
        Swal.fire({ icon: "error", title: "Error", text: "Failed to update status." });
      });
  };

  // ══════════════════════════════════════════════════════
  // OTP-based delivery 
  // ══════════════════════════════════════════════════════
  const requestDeliveryOtp = (jobId) => {
    setSendingOtp(true);
    const formData = new FormData();
    formData.append("id", jobId);

    axios
      .post(`${baseUrl}/delivery/jobs/sendDeliveryOtp`, formData, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((res) => {
        setSendingOtp(false);
        if (res.data.success) {
          setOtpJobId(jobId);
          setOtpValue("");
          Swal.fire({
            icon: "success",
            title: "OTP Sent",
            text: "A confirmation code has been sent to the customer.",
          });
        } else {
          Swal.fire({ icon: "error", title: "Error", text: res.data.message });
        }
      })
      .catch((error) => {
        setSendingOtp(false);
        if (error.response?.status === 401) {
          window.location.href = "/delivery/login";
          return;
        }
        Swal.fire({ icon: "error", title: "Error", text: "Failed to send OTP." });
      });
  };

  const verifyDeliveryOtp = () => {
    if (!otpValue.trim()) return;

    setVerifyingOtp(true);
    const formData = new FormData();
    formData.append("id", otpJobId);
    formData.append("otp", otpValue.trim());

    axios
      .post(`${baseUrl}/delivery/jobs/verifyDeliveryOtp`, formData, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((res) => {
        setVerifyingOtp(false);
        if (res.data.success) {
          Swal.fire({
            icon: "success",
            title: "Delivered!",
            text: "Delivery confirmed successfully.",
          });
          setOtpJobId(null);
          setOtpValue("");
          closeJobModal();
          viewMyJobs();
        } else {
          Swal.fire({ icon: "error", title: "Error", text: res.data.message });
        }
      })
      .catch((error) => {
        setVerifyingOtp(false);
        if (error.response?.status === 401) {
          window.location.href = "/delivery/login";
          return;
        }
        Swal.fire({ icon: "error", title: "Error", text: "Failed to verify OTP." });
      });
  };

  useEffect(() => {
    document.body.style.overflow =
      selectedJob || selectedReview || otpJobId ? "hidden" : "auto";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [selectedJob, selectedReview, otpJobId]);

  const filteredJobs =
    activeTab === "all"
      ? allJobs
      : allJobs.filter((job) => normalizeStatus(job.status) === activeTab);

  const getCount = (key) =>
    key === "all"
      ? allJobs.length
      : allJobs.filter((j) => normalizeStatus(j.status) === key).length;

  if (loading) {
    return (
      <DeliveryPanelLayout>
        <MyJobsPageSkeleton />
      </DeliveryPanelLayout>
    );
  }

  return (
    <DeliveryPanelLayout>
      <Container fluid className="px-0">
        {/* Page Header */}
        <div className="mj-page__header">
          <h4 className="mj-page__title">My Jobs</h4>
          <p className="mj-page__subtitle">
            All delivery orders assigned to you by customers
          </p>
        </div>

        {/* Filter Tabs */}
        <div className="mj-tabs">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              className={`mj-tab ${activeTab === tab.key ? "mj-tab--active" : ""}`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
              <span className="mj-tab__count">{getCount(tab.key)}</span>
            </button>
          ))}
        </div>

        {/* Job Cards Grid */}
        {filteredJobs.length === 0 ? (
          <div className="mj-empty">
            <div className="mj-empty__icon">
              <MdDeliveryDining />
            </div>
            <h6 className="mj-empty__title">No jobs found</h6>
            <p className="mj-empty__text">
              There are no {activeTab !== "all" ? activeTab : ""} jobs right
              now.
            </p>
          </div>
        ) : (
          <Row className="g-3">
            {filteredJobs.map((job) => (
              <Col key={job.id} xs={12}>
                <JobCard
                  orderId={`#KM-${job.kmorder?.id}`}
                  jobId={job.id}
                  changeJobStatus={updateJobStatus}
                  status={normalizeStatus(job.status)}
                  customerName={job.kmcustomer?.name}
                  customerPhone={job.kmcustomer?.phone}
                  address={job.kmorder?.shippingAddress}
                  date={job.created_at?.split("T")[0]}
                  totalAmount={job.kmorder?.totalPrice}
                  deliveryFee={job.final_delivery_charge}
                  review={{
                    rating: job.kmorder?.rating,
                    comment: job.kmorder?.review,
                  }}
                  onViewDetail={() => setSelectedJob(job)}
                  onReviewClick={() => setSelectedReview(job)}
                  onMarkPickedUp={() => {
                    setSelectedJob(job);
                    setCaptureMode("pickup");
                  }}
                  onMarkArrived={() => markArrived(job.id)}
                  onRequestOtp={() => requestDeliveryOtp(job.id)}
                />
              </Col>
            ))}
          </Row>
        )}
      </Container>

      {/* ── Order Detail Modal — defined right here in MyJob.jsx ── */}
      {selectedJob && (
        <div
          className="odm-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeJobModal();
          }}
        >
          <div className="odm-modal">
            {/* Header */}
            <div className="odm-modal__header">
              <h5 className="odm-modal__title">
                Order {selectedJob.kmorder?.id}
              </h5>
              <button
                className="odm-modal__close"
                onClick={closeJobModal}
              >
                <MdClose />
              </button>
            </div>

            {/* Body */}
            <div className="odm-modal__body">
              {/* Order Info */}
              <div>
                <h6 className="odm-section__title">Order Information</h6>
                <div className="odm-info-row">
                  <MdReceiptLong className="odm-info-icon" />
                  <span className="odm-info-label">Order ID:</span>
                  <span className="odm-info-value">{`#KM-${selectedJob.kmorder?.id}`}</span>
                </div>
                <div className="odm-info-row">
                  <MdCalendarToday className="odm-info-icon" />
                  <span className="odm-info-label">Assigned On:</span>
                  <span className="odm-info-value">
                    {selectedJob?.created_at
                      ? new Date(selectedJob.created_at).toLocaleString(
                          "en-PK",
                          {
                            timeZone: "Asia/Karachi",
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: true,
                          },
                        )
                      : "-"}
                  </span>
                </div>
                {normalizeStatus(selectedJob.status) === "completed" && (
                  <div className="odm-info-row">
                    <MdCheckCircle className="odm-info-icon" />
                    <span className="odm-info-label">Completed On:</span>
                    <span className="odm-info-value">
                      {new Date(selectedJob.updated_at).toLocaleString(
                        "en-PK",
                        {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                          hour12: true,
                        },
                      )}
                    </span>
                  </div>
                )}
                {normalizeStatus(selectedJob.status) === "returned" && (
                  <div className="odm-info-row">
                    <MdAssignmentReturn className="odm-info-icon" />
                    <span className="odm-info-label">Returned On:</span>
                    <span className="odm-info-value">
                      {new Date(selectedJob.updated_at).toLocaleString(
                        "en-PK",
                        {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                          hour12: true,
                        },
                      )}
                    </span>
                  </div>
                )}
                <div className="odm-info-row">
                  <MdShoppingBag className="odm-info-icon" />
                  <span className="odm-info-label">Products:</span>
                </div>

                {/* ── Product Images + Quantity + Price ── */}
                <div className="odm-product-images">
                  {selectedJob.kmorder?.kmcarts?.map((cart, idx) => (
                    <div key={idx} className="odm-product-img-wrap">
                      <img
                        src={`${baseUrlForFiles}/${cart.kmproduct?.image}`}
                        alt={cart.kmproduct?.name}
                        className="odm-product-img"
                      />
                      {/* Product naam */}
                      <span className="odm-product-img-label">
                        {cart.kmproduct?.name}
                      </span>
                      {/* Quantity — customer ne kitna order kiya */}
                      <span
                        className="odm-product-img-label"
                        style={{
                          color: "var(--text-muted)",
                          fontSize: "0.75rem",
                        }}
                      >
                        Qty: {cart.quantity} {cart.kmproduct?.unit}
                      </span>
                      {/* Price — quantity x amountPerPiece = total_price */}
                      <span
                        className="odm-product-img-label"
                        style={{
                          color: "var(--secondary-color)",
                          fontWeight: 600,
                          fontSize: "0.8rem",
                        }}
                      >
                        PKR {Number(cart.total_price || 0).toLocaleString()}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Order Total — sab products ka total milake */}
                <div
                  className="odm-amount-row"
                  style={{
                    marginTop: 10,
                    paddingTop: 8,
                    borderTop: "1px solid var(--border-light)",
                  }}
                >
                  <span
                    className="odm-amount-label"
                    style={{ fontWeight: 600 }}
                  >
                    Order Total
                  </span>
                  <span
                    className="odm-amount-value odm-amount-value--green"
                    style={{ fontWeight: 700 }}
                  >
                    PKR
                    {selectedJob.kmorder?.kmcarts
                      ?.reduce((sum, c) => sum + Number(c.total_price || 0), 0)
                      .toLocaleString()}
                  </span>
                </div>
              </div>

              {/* Seller / Shop Info */}
              <div>
                <h6 className="odm-section__title">Seller Information</h6>
                <div className="odm-info-row">
                  <MdStorefront className="odm-info-icon" />
                  <span className="odm-info-label">Shop Name:</span>
                  <span className="odm-info-value">
                    {selectedJob.kmorder?.kmseller?.shop_name}
                  </span>
                </div>
                <div className="odm-info-row">
                  <MdPhone className="odm-info-icon" />
                  <span className="odm-info-label">Phone:</span>
                  <span className="odm-info-value">
                    {
                      selectedJob.kmorder?.kmseller?.kmbusinessdetails
                        ?.phonenumber
                    }
                  </span>
                </div>
                <div className="odm-info-row">
                  <MdLocationOn className="odm-info-icon" />
                  <span className="odm-info-label">Shop Address:</span>
                  <span className="odm-info-value">
                    {selectedJob.kmorder?.kmseller?.kmbusinessdetails?.address}
                  </span>
                </div>
              </div>

              {/* Customer Info */}
              <div>
                <h6 className="odm-section__title">Customer Information</h6>
                <div className="odm-info-row">
                  <MdPerson className="odm-info-icon" />
                  <span className="odm-info-label">Name:</span>
                  <span className="odm-info-value">
                    {selectedJob.kmcustomer?.name}
                  </span>
                </div>
                <div className="odm-info-row">
                  <MdPhone className="odm-info-icon" />
                  <span className="odm-info-label">Phone:</span>
                  <span className="odm-info-value">
                    {selectedJob.kmcustomer?.phone}
                  </span>
                </div>
                <div className="odm-info-row">
                  <MdLocationOn className="odm-info-icon" />
                  <span className="odm-info-label">Address:</span>
                  <span className="odm-info-value">
                    {selectedJob.kmorder?.shippingAddress}
                  </span>
                </div>
              </div>

              {/* Amounts */}
              <div>
                <h6 className="odm-section__title">Payment Details</h6>
                <div className="odm-amount-row">
                  <span className="odm-amount-label">Order Total</span>
                  <span className="odm-amount-value odm-amount-value--green">
                    PKR {selectedJob.kmorder?.totalPrice?.toLocaleString()}
                  </span>
                </div>
                <div className="odm-amount-row">
                  <span className="odm-amount-label">Your Delivery Fee</span>
                  <span className="odm-amount-value odm-amount-value--blue">
                    PKR {selectedJob.final_delivery_charge?.toLocaleString()}
                  </span>
                </div>
                <div className="odm-amount-row odm-amount-row--grand">
                  <span className="odm-amount-label">Grand Total</span>
                  <span className="odm-amount-value">
                    PKR
                    {(
                      Number(selectedJob.kmorder?.totalPrice || 0) +
                      Number(selectedJob.final_delivery_charge || 0)
                    ).toLocaleString()}
                  </span>
                </div>
              </div>
            </div>
            {normalizeStatus(selectedJob.status) === "ready_for_pickup" && (
              <div className="odm-modal__footer" style={{ flexDirection: "column", alignItems: "stretch", gap: 10 }}>
                {captureMode === "pickup" ? (
                  <div className="odm-capture-form">
                    <label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 6 }}>
                      <MdCameraAlt style={{ marginRight: 6, verticalAlign: "middle" }} />
                      Attach a photo of the picked-up product
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          setPickupImageFile(file);
                          setPickupImagePreview(URL.createObjectURL(file));
                          setPickupImageError("");
                        }
                      }}
                      style={{
                        display: "block",
                        marginBottom: 8,
                        borderColor: pickupImageError ? "#dc3545" : undefined,
                      }}
                    />
                    {pickupImageError && (
                      <p style={{ color: "#dc3545", fontSize: 12, marginBottom: 8 }}>
                        {pickupImageError}
                      </p>
                    )}
                    {pickupImagePreview && (
                      <img
                        src={pickupImagePreview}
                        alt="Pickup preview"
                        style={{
                          width: 120,
                          height: 120,
                          objectFit: "cover",
                          borderRadius: 8,
                          marginBottom: 10,
                          display: "block",
                        }}
                      />
                    )}

                    <label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 6 }}>
                      Estimated delivery time
                    </label>
                    <select
                      value={pickupEta}
                      onChange={(e) => {
                        setPickupEta(e.target.value);
                        setPickupEtaError("");
                      }}
                      style={{
                        display: "block",
                        width: "100%",
                        padding: "8px 10px",
                        marginBottom: 8,
                        borderRadius: 8,
                        border: pickupEtaError ? "1px solid #dc3545" : "1px solid var(--border-light)",
                      }}
                    >
                      <option value="">-- Select estimated delivery time --</option>
                      {ETA_OPTIONS.map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                    {pickupEtaError && (
                      <p style={{ color: "#dc3545", fontSize: 12, marginBottom: 8 }}>
                        {pickupEtaError}
                      </p>
                    )}

                    <div style={{ display: "flex", gap: 8 }}>
                      <button
                        className="mj-mark-returned-btn"
                        style={{ background: "transparent", color: "var(--secondary-color)", border: "1px solid var(--border-light)" }}
                        onClick={() => { setCaptureMode(null); resetPickupCapture(); }}
                        disabled={markingPickedUp}
                      >
                        Cancel
                      </button>
                      <button
                        className="mj-mark-returned-btn"
                        onClick={handleConfirmPickup}
                        disabled={markingPickedUp}
                      >
                        {markingPickedUp ? "Updating..." : "Confirm Pickup"}
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    className="mj-mark-returned-btn"
                    onClick={() => setCaptureMode("pickup")}
                  >
                    <MdLocalShipping size={16} /> Confirm Pickup
                  </button>
                )}
              </div>
            )}
            {normalizeStatus(selectedJob.status) === "shipping" && (
              <div className="odm-modal__footer">
                <button
                  className="mj-mark-returned-btn"
                  onClick={() => markArrived(selectedJob.id)}
                >
                  <MdLocalShipping size={16} /> I've Arrived
                </button>
              </div>
            )}
            {normalizeStatus(selectedJob.status) === "arrived" && (
              <div className="odm-modal__footer">
                <button
                  className="mj-mark-returned-btn"
                  onClick={() => requestDeliveryOtp(selectedJob.id)}
                  disabled={sendingOtp}
                >
                  <MdAssignmentReturn size={16} />{" "}
                  {sendingOtp ? "Sending..." : "Mark Completed"}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Review Modal — defined right here in MyJob.jsx ── */}
      {selectedReview && (
        <div
          className="odm-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) setSelectedReview(null);
          }}
        >
          <div className="odm-modal" style={{ maxWidth: "440px" }}>
            {/* Header */}
            <div className="odm-modal__header">
              <h5 className="odm-modal__title">Customer Review</h5>
              <button
                className="odm-modal__close"
                onClick={() => setSelectedReview(null)}
              >
                <MdClose />
              </button>
            </div>

            {/* Body */}
            <div className="odm-modal__body">
              <div className="rvm-customer-row">
                <div className="rvm-avatar">
                  {getInitials(selectedReview.customerName)}
                </div>
                <div>
                  <p className="rvm-customer-name">
                    {selectedReview.customerName}
                  </p>
                  <p className="rvm-order-id">Order {selectedReview.orderId}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* ── OTP Confirmation Modal ── */}
      {otpJobId && (
        <div
          className="odm-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setOtpJobId(null);
              setOtpValue("");
            }
          }}
        >
          <div className="odm-modal" style={{ maxWidth: "380px" }}>
            <div className="odm-modal__header">
              <h5 className="odm-modal__title">Enter Delivery Code</h5>
              <button
                className="odm-modal__close"
                onClick={() => {
                  setOtpJobId(null);
                  setOtpValue("");
                }}
              >
                <MdClose />
              </button>
            </div>

            <div className="odm-modal__body">
              <p style={{ fontSize: 14, color: "var(--text-muted)", marginBottom: 12 }}>
                Ask the customer for the confirmation code sent to their
                account, then enter it below to complete the delivery.
              </p>
              <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={otpValue}
                onChange={(e) => setOtpValue(e.target.value.replace(/\D/g, ""))}
                placeholder="Enter 6-digit code"
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  fontSize: 18,
                  letterSpacing: 4,
                  textAlign: "center",
                  border: "1px solid var(--border-light)",
                  borderRadius: 8,
                }}
              />
            </div>

            <div className="odm-modal__footer" style={{ display: "flex", gap: 8 }}>
              <button
                className="mj-mark-returned-btn"
                style={{ background: "transparent", color: "var(--secondary-color)", border: "1px solid var(--border-light)" }}
                onClick={() => requestDeliveryOtp(otpJobId)}
                disabled={sendingOtp}
              >
                {sendingOtp ? "Resending..." : "Resend Code"}
              </button>
              <button
                className="mj-mark-returned-btn"
                onClick={verifyDeliveryOtp}
                disabled={verifyingOtp || otpValue.trim().length !== 6}
              >
                {verifyingOtp ? "Verifying..." : "Confirm Delivery"}
              </button>
            </div>
          </div>
        </div>
      )}
    </DeliveryPanelLayout>
  );
};

export default MyJobs;
